import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

// THE WIDTH THE READER'S OVERLAY IS LAID OUT AT: the picture's box on a phone held upright (an
// iPhone Pro Max's, 396 px), which is where the caption, its arrows and the play control were
// sized and look right.
const READER_WIDTH = 396;
// AND ITS HEIGHT, the 9:16 box at that width (see shotShape).
const READER_HEIGHT = READER_WIDTH * 16 / 9;

// WHAT IS DRAWN OVER THE PICTURE, AT ONE SIZE FOR EVERY BOX: laid out at READER_WIDTH, then scaled
// to the box it is in. Sized in pixels and against the window, the same sentence wrapped to four
// lines in Phase 6's box, two on a desktop and three on a phone, and covered a different share of
// the picture each time (Louis 2026-10-28). Scaled, it breaks where the phone breaks it and takes
// the same share of the picture everywhere, as the picture under it does (see SHOT_ASPECT).
//
// IN THE READER'S FONT, Satoshi, set here rather than inherited: the published page sets it on
// the whole page and the back office does not, and the app's wider face broke the same sentence
// onto four lines where the phone has three (Louis 2026-10-28).
//
// Taking no press itself: what is inside says where it takes one (pointer-events-auto).
export function ReaderScale({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const fit = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  // BY WHICHEVER SIDE IS SHORTER OF THE PHONE'S: a desktop's box (see formFactor) is the phone's
  // height and wider, and scaled by its width the caption covered half the picture. Laid out as wide
  // as the box then is, at the phone's size.
  const scale = size?.w && size.h ? Math.min(size.w / READER_WIDTH, size.h / READER_HEIGHT) : 1;
  return (
    <div ref={box} className="pointer-events-none absolute inset-0 overflow-hidden font-satoshi">
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ width: size ? size.w / scale : READER_WIDTH, height: size ? size.h / scale : '100%', transform: `scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
}
